import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Info, Printer } from "lucide-react";
import { encounterQueryKeys, encountersApi } from "../../../shared/api/encounters";
import { prescriptionsApi } from "../../../shared/api/prescriptions";
import { apiError } from "../../../shared/api/client";
import { PageHeader } from "../../../shared/ui/PageHeader";
import { ErrorState } from "../../../shared/ui/ErrorState";
import { Skeleton } from "../../../shared/ui/Skeleton";
import { useToast } from "../../../shared/ui/Toast";
import { useKeyboardShortcut } from "../../../shared/ui/useKeyboardShortcut";
import { Button } from "@/components/ui/button";
import { EncounterHeader } from "./EncounterHeader";
import { ExamModePanel } from "./ExamModePanel";
import { EncounterTabs } from "./EncounterTabs";

interface Props {
  encounterId: string;
  children: (data: NonNullable<Awaited<ReturnType<typeof encountersApi.get>>>) => ReactNode;
}

/**
 * Common shell for every `/encounters/:id/*` sub-page: loads the encounter,
 * renders header + tabs, and yields the loaded detail to the page body. Each
 * sub-page just owns its tab content.
 */
export function EncounterShell({ encounterId, children }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const detail = useQuery({
    queryKey: encounterQueryKeys.detail(encounterId),
    queryFn: () => encountersApi.get(encounterId),
  });

  const sign = useMutation({
    mutationFn: async () => {
      await encountersApi.signEncounter(encounterId);
      // Auto-promote the Rx draft into an official prescription so it shows up in
      // the Đơn thuốc list without a separate "kê đơn chính thức" step. Idempotent
      // per encounter + best-effort — an empty draft just no-ops.
      const hasRx = (detail.data?.prescription?.items?.length ?? 0) > 0;
      if (hasRx) {
        try {
          await prescriptionsApi.createFromEncounter(encounterId);
        } catch {
          /* no valid Rx to promote (e.g. a line without a catalogue medicine) */
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: encounterQueryKeys.detail(encounterId) });
      qc.invalidateQueries({ queryKey: encounterQueryKeys.queue() });
      qc.invalidateQueries({ queryKey: ["prescriptions"] });
      toast.success("Đã ký + kết thúc lần khám.");
    },
    onError: (e) => toast.error(apiError(e).detail),
  });

  const cancel = useMutation({
    mutationFn: (reason: string) => encountersApi.cancelEncounter(encounterId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: encounterQueryKeys.detail(encounterId) });
      qc.invalidateQueries({ queryKey: encounterQueryKeys.queue() });
      navigate("/encounters");
    },
  });

  // ---- Keyboard shortcuts (M27) ------------------------------------
  // Tab navigation 1-5. Single-key by design; hook ignores keystrokes
  // inside text inputs so typing "1" in the SOAP editor doesn't navigate.
  useKeyboardShortcut(
    { key: "1" },
    () => navigate(`/encounters/${encounterId}/vitals`),
    [encounterId]
  );
  useKeyboardShortcut(
    { key: "2" },
    () => navigate(`/encounters/${encounterId}/notes`),
    [encounterId]
  );
  useKeyboardShortcut(
    { key: "3" },
    () => navigate(`/encounters/${encounterId}/diagnoses`),
    [encounterId]
  );
  useKeyboardShortcut(
    { key: "4" },
    () => navigate(`/encounters/${encounterId}/prescription`),
    [encounterId]
  );
  useKeyboardShortcut(
    { key: "5" },
    () => navigate(`/encounters/${encounterId}/attachments`),
    [encounterId]
  );

  // Cmd/Ctrl+P → print visit summary (overrides browser default which would
  // dump the React app shell).
  useKeyboardShortcut(
    { key: "p", mod: true, allowInInput: true },
    (ev) => {
      ev.preventDefault();
      navigate(`/encounters/${encounterId}/print`);
    },
    [encounterId]
  );

  // Cmd/Ctrl+Shift+S → sign + finish. Declared BEFORE the loading/error early
  // returns so the hook count is stable across renders (React error #310). The
  // handler guards on the loaded data + IN_PROGRESS state itself.
  useKeyboardShortcut(
    { key: "s", mod: true, shift: true, allowInInput: true },
    (ev) => {
      ev.preventDefault();
      const data = detail.data;
      if (!data || data.status !== "IN_PROGRESS") return;
      if (data.diagnoses.length === 0) {
        toast.warn("Vui lòng thêm ít nhất 1 chẩn đoán trước khi ký.");
        navigate(`/encounters/${encounterId}/diagnoses`);
        return;
      }
      if (confirm("Ký và kết thúc lần khám? Sau khi ký, nội dung sẽ bị khoá.")) {
        sign.mutate();
      }
    },
    [encounterId, detail.data]
  );

  if (detail.isLoading) {
    return (
      <div>
        <PageHeader title="Đang tải lần khám…" />
        <Skeleton height={160} />
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div>
        <PageHeader
          title="Không tìm thấy lần khám"
          breadcrumbs={[{ label: "Trang chủ", to: "/dashboard" }, { label: "Lần khám", to: "/encounters" }]}
        />
        <ErrorState error={detail.error}>
          <Button asChild variant="outline">
            <Link to="/encounters">← Danh sách</Link>
          </Button>
        </ErrorState>
      </div>
    );
  }

  const e = detail.data;
  const isReadOnly = e.status !== "IN_PROGRESS";

  const headerActions =
    e.status === "IN_PROGRESS" ? (
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            const reason = prompt("Lý do hủy lần khám?");
            if (reason && reason.trim()) cancel.mutate(reason.trim());
          }}
          disabled={cancel.isPending}
        >
          Hủy
        </Button>
        <Button
          type="button"
          onClick={() => {
            if (e.diagnoses.length === 0) {
              toast.warn("Vui lòng thêm ít nhất 1 chẩn đoán trước khi ký.");
              navigate(`/encounters/${encounterId}/diagnoses`);
              return;
            }
            if (confirm("Ký và kết thúc lần khám? Sau khi ký, nội dung sẽ bị khoá.")) {
              sign.mutate();
            }
          }}
          disabled={sign.isPending}
          title="Yêu cầu MFA + chữ ký số (M9). Hiện đang dùng mock."
        >
          {sign.isPending ? "Đang ký…" : "Ký + kết thúc"}
        </Button>
      </div>
    ) : (
      <div className="flex gap-2">
        <Button asChild variant="outline" title="Phiếu tóm tắt A5 cho bệnh nhân">
          <Link to={`/encounters/${encounterId}/print`}>
            <Printer className="size-4" />
            In phiếu tóm tắt
          </Link>
        </Button>
        {e.prescription.pdfUrl && (
          <Button asChild variant="outline">
            <a href={e.prescription.pdfUrl} target="_blank" rel="noopener noreferrer">
              Xem đơn thuốc PDF
            </a>
          </Button>
        )}
      </div>
    );

  return (
    <div>
      <PageHeader
        title={`Lần khám ${e.patientName}`}
        breadcrumbs={[
          { label: "Trang chủ", to: "/dashboard" },
          { label: "Lần khám", to: "/encounters" },
          { label: e.patientName },
        ]}
        actions={
          <Button asChild variant="outline">
            <Link to="/encounters">← Hàng chờ</Link>
          </Button>
        }
      />
      <EncounterHeader encounter={e} rightActions={headerActions} />
      <ExamModePanel encounterId={encounterId} />
      <EncounterTabs
        encounterId={encounterId}
        hasVitals={e.hasVitals}
        diagnosisCount={e.diagnoses.length}
        prescriptionItemCount={e.prescription.items.length}
        attachmentCount={e.attachments.length}
      />
      {isReadOnly && (
        <div
          className="mb-4 flex items-start gap-2 rounded-md border border-input bg-muted px-4 py-3 text-sm text-muted-foreground"
          role="status"
        >
          <Info className="mt-0.5 size-4 shrink-0 text-primary" />
          <span>Lần khám đã ký — nội dung chỉ đọc. Để chỉnh sửa, tạo bản amendment.</span>
        </div>
      )}
      {children(e)}
    </div>
  );
}
